import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Text,
  Tooltip,
  InputGroup,
  InputRightElement,
  Grid,
  GridItem,
  Heading,
  Divider,
  HStack,
} from '@chakra-ui/react';
import 'react-datepicker/dist/react-datepicker.css';
import './datepicker-custom.css';
import moment from 'moment';
import 'moment/locale/ar-sa';
import momentHijri from 'moment-hijri';
import { CalendarIcon } from '@chakra-ui/icons';

// مكون اختيار التاريخ المخصص مع دعم التقويم الهجري والميلادي
const CustomDatePicker = ({ value, onChange, name, error, isRequired }) => {
  // إعداد اللغة العربية للتواريخ
  moment.locale('ar-sa');
  
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [calendarType, setCalendarType] = useState('hijri'); // البدء بالتقويم الهجري كافتراضي
  const [age, setAge] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hijriDate, setHijriDate] = useState(() => {
    // تهيئة التاريخ الهجري الافتراضي
    const mhijri = momentHijri();
    return {
      day: mhijri.iDate(),
      month: mhijri.iMonth(),
      year: mhijri.iYear()
    };
  });
  
  // حساب العمر بناءً على تاريخ الميلاد
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    
    let age = today.getFullYear() - birthDateObj.getFullYear();
    const monthDiff = today.getMonth() - birthDateObj.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
      age--;
    }
    
    return age;
  };
  
  // معالجة تغيير التاريخ
  const handleDateChange = (date) => {
    setSelectedDate(date);
    
    if (date) {
      // تحديث التاريخ الهجري المقابل
      const mhijri = momentHijri(date);
      setHijriDate({
        day: mhijri.iDate(),
        month: mhijri.iMonth(),
        year: mhijri.iYear()
      });
      
      // حساب العمر
      const calculatedAge = calculateAge(date);
      setAge(calculatedAge);
      
      // إرسال التاريخ بتنسيق ISO للتخزين
      onChange({ target: { name, value: date.toISOString() } });
    } else {
      setAge(null);
      onChange({ target: { name, value: null } });
    }
  };
  
  // معالجة تغيير التاريخ الهجري
  const handleHijriDateChange = (type, value) => {
    const newHijriDate = { ...hijriDate, [type]: parseInt(value, 10) };
    setHijriDate(newHijriDate);
    
    // تحويل التاريخ الهجري إلى ميلادي
    try {
      const mhijri = momentHijri()
        .iYear(newHijriDate.year)
        .iMonth(newHijriDate.month)
        .iDate(newHijriDate.day);
      
      const gregorianDate = mhijri.toDate();
      handleDateChange(gregorianDate);
    } catch (error) {
      console.error('Error converting Hijri date:', error);
    }
  };
  
  // تبديل نوع التقويم بين الميلادي والهجري
  const toggleCalendarType = () => {
    setCalendarType(prevType => prevType === 'gregorian' ? 'hijri' : 'gregorian');
  };
  
  // تنسيق التاريخ بناءً على نوع التقويم
  const formatDate = (date) => {
    if (!date) return "";
    
    try {
      if (calendarType === 'gregorian') {
        return moment(date).format('DD/MM/YYYY');
      } else {
        // استخدام التاريخ الهجري المخزن
        return `${hijriDate.day}/${hijriDate.month + 1}/${hijriDate.year}`;
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };
  
  // تحديد التاريخ المعروض في المدخل
  const displayDate = selectedDate ? formatDate(selectedDate) : '';
  
  // إنشاء قوائم للأيام والشهور والسنوات
  // قوائم مشتركة للأيام
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  
  // قوائم الشهور الهجرية
  const hijriMonths = [
    { value: 0, label: 'محرم' },
    { value: 1, label: 'صفر' },
    { value: 2, label: 'ربيع الأول' },
    { value: 3, label: 'ربيع الثاني' },
    { value: 4, label: 'جمادى الأولى' },
    { value: 5, label: 'جمادى الثانية' },
    { value: 6, label: 'رجب' },
    { value: 7, label: 'شعبان' },
    { value: 8, label: 'رمضان' },
    { value: 9, label: 'شوال' },
    { value: 10, label: 'ذو القعدة' },
    { value: 11, label: 'ذو الحجة' }
  ];
  
  // قوائم الشهور الميلادية
  const gregorianMonths = [
    { value: 0, label: 'يناير' },
    { value: 1, label: 'فبراير' },
    { value: 2, label: 'مارس' },
    { value: 3, label: 'أبريل' },
    { value: 4, label: 'مايو' },
    { value: 5, label: 'يونيو' },
    { value: 6, label: 'يوليو' },
    { value: 7, label: 'أغسطس' },
    { value: 8, label: 'سبتمبر' },
    { value: 9, label: 'أكتوبر' },
    { value: 10, label: 'نوفمبر' },
    { value: 11, label: 'ديسمبر' }
  ];
  
  // قوائم السنوات - بداية من 20 سنة للخلف
  const currentHijriYear = momentHijri().iYear();
  const currentGregorianYear = new Date().getFullYear();
  // إنشاء قائمة السنوات بحيث تبدأ من 20 سنة للخلف وتمتد إلى 80 سنة للخلف
  const hijriYears = Array.from({ length: 60 }, (_, i) => currentHijriYear - 80 + i);
  const gregorianYears = Array.from({ length: 60 }, (_, i) => currentGregorianYear - 80 + i);
  
  // التعامل مع فتح وإغلاق التقويم
  const handleCalendarToggle = () => {
    setIsOpen(!isOpen);
  };
  
  return (
    <FormControl isInvalid={error} isRequired={isRequired} className="custom-date-picker-container" dir="rtl">
      <FormLabel htmlFor={name} fontWeight="bold">تاريخ الميلاد</FormLabel>
      <div className="date-picker-wrapper">
        <InputGroup dir="rtl">
          <Input
            id={name}
            autoComplete="bday"
            placeholder={calendarType === 'gregorian' ? "DD/MM/YYYY" : "يوم/شهر/سنة هجري"}
            value={displayDate}
            onClick={handleCalendarToggle}
            readOnly
            borderColor="gray.300"
            _hover={{ borderColor: "teal.300" }}
            textAlign="center"
            dir="rtl"
          />
          <InputRightElement>
            <CalendarIcon color="teal.500" cursor="pointer" onClick={handleCalendarToggle} />
          </InputRightElement>
        </InputGroup>
        
        {/* عرض التقويم عند النقر */}
        {isOpen && (
          <Box 
            className="calendar-popup" 
            position="absolute" 
            zIndex="1000" 
            mt={2} 
            bg="white" 
            borderRadius="md" 
            boxShadow="xl"
            overflow="hidden"
          >
            {calendarType === 'gregorian' ? (
              <Box 
                className="gregorian-calendar" 
                bg="white" 
                borderRadius="md" 
                boxShadow="md" 
                p={4} 
                width="100%" 
                maxWidth="350px" 
                dir="rtl"
              >
                <Heading as="h4" size="md" mb={3} textAlign="center">
                  اختيار التاريخ الميلادي
                </Heading>
                <Divider mb={4} />
                
                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" textAlign="center">
                        اليوم
                      </FormLabel>
                      <Select 
                        value={selectedDate ? selectedDate.getDate() : ''} 
                        onChange={(e) => {
                          if (selectedDate) {
                            const newDate = new Date(selectedDate);
                            newDate.setDate(parseInt(e.target.value, 10));
                            handleDateChange(newDate);
                          } else {
                            const newDate = new Date();
                            newDate.setDate(parseInt(e.target.value, 10));
                            handleDateChange(newDate);
                          }
                        }}
                        size="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: "teal.300" }}
                        textAlign="center"
                        dir="rtl"
                      >
                        {days.map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" textAlign="center">
                        الشهر
                      </FormLabel>
                      <Select 
                        value={selectedDate ? selectedDate.getMonth() : ''} 
                        onChange={(e) => {
                          if (selectedDate) {
                            const newDate = new Date(selectedDate);
                            newDate.setMonth(parseInt(e.target.value, 10));
                            handleDateChange(newDate);
                          } else {
                            const newDate = new Date();
                            newDate.setMonth(parseInt(e.target.value, 10));
                            handleDateChange(newDate);
                          }
                        }}
                        size="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: "teal.300" }}
                        textAlign="center"
                        dir="rtl"
                      >
                        {gregorianMonths.map(month => (
                          <option key={month.value} value={month.value}>{month.label}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" textAlign="center">
                        السنة
                      </FormLabel>
                      <Select 
                        value={selectedDate ? selectedDate.getFullYear() : ''} 
                        onChange={(e) => {
                          if (selectedDate) {
                            const newDate = new Date(selectedDate);
                            newDate.setFullYear(parseInt(e.target.value, 10));
                            handleDateChange(newDate);
                          } else {
                            const newDate = new Date();
                            newDate.setFullYear(parseInt(e.target.value, 10));
                            handleDateChange(newDate);
                          }
                        }}
                        size="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: "teal.300" }}
                        textAlign="center"
                        dir="rtl"
                      >
                        {gregorianYears.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
                
                <Button 
                  mt={2} 
                  size="md" 
                  colorScheme="teal" 
                  width="100%" 
                  onClick={() => setIsOpen(false)}
                  borderRadius="md"
                  _hover={{ bg: "teal.600" }}
                >
                  تم اختيار التاريخ
                </Button>
              </Box>
            ) : (
              <Box 
                className="hijri-calendar" 
                bg="white" 
                borderRadius="md" 
                boxShadow="md" 
                p={4} 
                width="100%" 
                maxWidth="350px" 
                dir="rtl"
              >
                <Heading as="h4" size="md" mb={3} textAlign="center">
                  اختيار التاريخ الهجري
                </Heading>
                <Divider mb={4} />
                
                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" textAlign="center">
                        اليوم
                      </FormLabel>
                      <Select 
                        value={hijriDate.day} 
                        onChange={(e) => handleHijriDateChange('day', e.target.value)}
                        size="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: "teal.300" }}
                        textAlign="center"
                        dir="rtl"
                      >
                        {days.map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" textAlign="center">
                        الشهر
                      </FormLabel>
                      <Select 
                        value={hijriDate.month} 
                        onChange={(e) => handleHijriDateChange('month', e.target.value)}
                        size="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: "teal.300" }}
                        textAlign="center"
                        dir="rtl"
                      >
                        {hijriMonths.map(month => (
                          <option key={month.value} value={month.value}>{month.label}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" textAlign="center">
                        السنة
                      </FormLabel>
                      <Select 
                        value={hijriDate.year} 
                        onChange={(e) => handleHijriDateChange('year', e.target.value)}
                        size="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: "teal.300" }}
                        textAlign="center"
                        dir="rtl"
                      >
                        {hijriYears.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
                
                <Button 
                  mt={2} 
                  size="md" 
                  colorScheme="teal" 
                  width="100%" 
                  onClick={() => setIsOpen(false)}
                  borderRadius="md"
                  _hover={{ bg: "teal.600" }}
                >
                  تم اختيار التاريخ
                </Button>
              </Box>
            )}
          </Box>
        )}
      </div>
      
      {/* عرض العمر والتبديل بين التقويمين */}
      
      <HStack mt={2} spacing={4} justify="space-between">
        <Button
          size="sm"
          onClick={toggleCalendarType}
          mr={2}
          colorScheme="teal"
          variant="outline"
          borderRadius="md"
          _hover={{ bg: "teal.50" }}
        >
          {calendarType === 'gregorian' ? 'هجري' : 'ميلادي'}
        </Button>
        {age !== null && (
          <Tooltip label="العمر المحسوب بناءً على تاريخ الميلاد" placement="top">
            <Text fontSize="sm" color="gray.600">
              العمر: {age} سنة
            </Text>
          </Tooltip>
        )}
      </HStack>
      
      {error && (
        <FormErrorMessage>{error.message}</FormErrorMessage>
      )}
    </FormControl>
  );
};

export default CustomDatePicker;
